import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  LinearProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
  Badge,
  Divider
} from '@mui/material';
import {
  Email as EmailIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Sync as SyncIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  CloudUpload as CloudUploadIcon,
  Assessment as AssessmentIcon,
  Mail as MailIcon,
  Campaign as CampaignIcon,
  Analytics as AnalyticsIcon,
  Autorenew as AutorenewIcon,
  Block as BlockIcon,
  Code as CodeIcon,
  ContactMail as ContactMailIcon,
  MoneyOff as MoneyOffIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const EmailProvidersPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState<any>({
    configured: [],
    available: [],
    activeProvider: 'internal'
  });
  
  // Dialog states
  const [configDialog, setConfigDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [configData, setConfigData] = useState<any>({});
  const [testResult, setTestResult] = useState<any>(null);
  const [syncStatus, setSyncStatus] = useState<any>({});
  const [healthStatus, setHealthStatus] = useState<any>({});

  // Statistics
  const [usageStats, setUsageStats] = useState<any>({});

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/email-providers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviders(response.data);
      
      // Fetch health status for configured providers
      for (const provider of response.data.configured) {
        fetchHealthStatus(provider.provider);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHealthStatus = async (provider: string) => {
    try {
      const response = await axios.get(`/api/email-providers/${provider}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHealthStatus((prev: any) => ({
        ...prev,
        [provider]: response.data.health
      }));
    } catch (error) {
      console.error('Error fetching health status:', error);
    }
  };

  const fetchUsageStats = async (provider: string) => {
    try {
      const response = await axios.get(`/api/email-providers/${provider}/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsageStats((prev: any) => ({
        ...prev,
        [provider]: response.data
      }));
    } catch (error) {
      console.error('Error fetching usage stats:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleConfigure = (provider: any) => {
    setSelectedProvider(provider);
    setConfigDialog(true);
    setConfigData({});
    setTestResult(null);
  };

  const handleTest = async () => {
    if (!selectedProvider) return;

    try {
      const response = await axios.post(
        `/api/email-providers/${selectedProvider.name}/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResult(response.data);
    } catch (error: any) {
      setTestResult({
        valid: false,
        error: error.response?.data?.error || error.message
      });
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedProvider) return;

    try {
      const response = await axios.post(
        `/api/email-providers/${selectedProvider.name}/configure`,
        configData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setConfigDialog(false);
      fetchProviders();
      
      // Show success message
      alert('Provider configured successfully!');
    } catch (error: any) {
      alert(`Configuration failed: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleSyncContacts = async (provider: string) => {
    try {
      await axios.post(
        `/api/email-providers/${provider}/sync-contacts`,
        { batchSize: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Start polling sync status
      pollSyncStatus(provider);
    } catch (error) {
      console.error('Error starting sync:', error);
    }
  };

  const pollSyncStatus = async (provider: string) => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(
          `/api/email-providers/${provider}/sync-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setSyncStatus((prev: any) => ({
          ...prev,
          [provider]: response.data
        }));
        
        if (!response.data.syncInProgress) {
          clearInterval(interval);
        }
      } catch (error) {
        clearInterval(interval);
      }
    }, 2000);
  };

  const handleCheckHealth = async (provider: string) => {
    try {
      const response = await axios.post(
        `/api/email-providers/${provider}/health-check`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setHealthStatus((prev: any) => ({
        ...prev,
        [provider]: response.data.health
      }));
    } catch (error) {
      console.error('Error checking health:', error);
    }
  };

  const handleSetDefault = async (provider: string) => {
    try {
      await axios.post(
        `/api/email-providers/${provider}/set-default`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchProviders();
    } catch (error) {
      console.error('Error setting default:', error);
    }
  };

  const handleDisable = async (provider: string) => {
    if (!window.confirm(`Are you sure you want to disable ${provider}?`)) return;

    try {
      await axios.post(
        `/api/email-providers/${provider}/disable`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchProviders();
    } catch (error) {
      console.error('Error disabling provider:', error);
    }
  };

  const getProviderIcon = (provider: string) => {
    const icons: any = {
      mailchimp: '🐵',
      sendgrid: '📧',
      mailgun: '🔫',
      activecampaign: '🚀',
      constantcontact: '📮',
      klaviyo: '💎',
      internal: '🏠'
    };
    return icons[provider] || '📧';
  };

  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon color="success" />;
      case 'degraded':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <ErrorIcon color="disabled" />;
    }
  };

  const getProviderConfigFields = (provider: string) => {
    const fields: any = {
      mailchimp: [
        { name: 'apiKey', label: 'API Key', type: 'password', required: true },
        { name: 'serverPrefix', label: 'Server Prefix', type: 'text', required: true, helperText: 'e.g., us1, us2' },
        { name: 'listId', label: 'List ID', type: 'text', required: true }
      ],
      sendgrid: [
        { name: 'apiKey', label: 'API Key', type: 'password', required: true }
      ],
      mailgun: [
        { name: 'apiKey', label: 'API Key', type: 'password', required: true },
        { name: 'domain', label: 'Domain', type: 'text', required: true }
      ],
      activecampaign: [
        { name: 'apiKey', label: 'API Key', type: 'password', required: true },
        { name: 'accountUrl', label: 'Account URL', type: 'text', required: true, helperText: 'https://account.api-us1.com' }
      ],
      constantcontact: [
        { name: 'apiKey', label: 'API Key', type: 'password', required: true },
        { name: 'accessToken', label: 'Access Token', type: 'password', required: true }
      ],
      klaviyo: [
        { name: 'apiKey', label: 'API Key', type: 'password', required: true },
        { name: 'listId', label: 'List ID', type: 'text', required: false }
      ]
    };
    
    return fields[provider] || [];
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Email Marketing Integration</Typography>
        <Chip 
          icon={<EmailIcon />}
          label={`Active: ${providers.activeProvider}`}
          color={providers.activeProvider === 'internal' ? 'default' : 'primary'}
        />
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab icon={<SettingsIcon />} label="Providers" />
          <Tab icon={<SyncIcon />} label="Sync Status" />
          <Tab icon={<AssessmentIcon />} label="Usage & Analytics" />
          <Tab icon={<CodeIcon />} label="Integration Guide" />
        </Tabs>
      </Paper>

      {/* Providers Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {providers.available.map((provider: any) => {
            const isConfigured = providers.configured.find((p: any) => p.provider === provider.name);
            const health = healthStatus[provider.name];
            const isActive = providers.activeProvider === provider.name;
            
            return (
              <Grid item xs={12} md={6} lg={4} key={provider.name}>
                <Card 
                  sx={{ 
                    height: '100%',
                    border: isActive ? '2px solid' : '1px solid',
                    borderColor: isActive ? 'primary.main' : 'divider'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h5">{getProviderIcon(provider.name)}</Typography>
                        <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                          {provider.name}
                        </Typography>
                      </Box>
                      {isConfigured && getHealthIcon(health?.status || 'unknown')}
                    </Box>

                    {isActive && (
                      <Chip 
                        label="Default Provider" 
                        color="primary" 
                        size="small" 
                        sx={{ mb: 1 }}
                      />
                    )}

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Features:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {provider.features.slice(0, 4).map((feature: string) => (
                          <Chip 
                            key={feature}
                            label={feature.replace(/_/g, ' ')}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {provider.features.length > 4 && (
                          <Chip 
                            label={`+${provider.features.length - 4} more`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>

                    {isConfigured && health && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Response Time: {health.responseTime}ms
                        </Typography>
                        {health.errorMessage && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            {health.errorMessage}
                          </Alert>
                        )}
                      </Box>
                    )}
                  </CardContent>
                  
                  <CardActions>
                    {!isConfigured ? (
                      <Button 
                        size="small" 
                        startIcon={<SettingsIcon />}
                        onClick={() => handleConfigure(provider)}
                      >
                        Configure
                      </Button>
                    ) : (
                      <>
                        <Button 
                          size="small" 
                          onClick={() => handleConfigure(provider)}
                        >
                          Edit
                        </Button>
                        {!isActive && (
                          <Button 
                            size="small" 
                            color="primary"
                            onClick={() => handleSetDefault(provider.name)}
                          >
                            Set Default
                          </Button>
                        )}
                        <IconButton 
                          size="small"
                          onClick={() => handleCheckHealth(provider.name)}
                          title="Check Health"
                        >
                          <AutorenewIcon />
                        </IconButton>
                        <IconButton 
                          size="small"
                          color="error"
                          onClick={() => handleDisable(provider.name)}
                          title="Disable"
                        >
                          <BlockIcon />
                        </IconButton>
                      </>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>

      {/* Sync Status Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {providers.configured.map((provider: any) => {
            const sync = syncStatus[provider.provider] || provider.syncStatus;
            
            return (
              <Grid item xs={12} md={6} key={provider.provider}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {getProviderIcon(provider.provider)} {provider.provider}
                    </Typography>
                    
                    {sync && (
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Last Sync:</Typography>
                          <Typography variant="body2">
                            {sync.lastSync ? new Date(sync.lastSync).toLocaleString() : 'Never'}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Synced Contacts:</Typography>
                          <Typography variant="body2">{sync.syncedContacts || 0}</Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body2">Failed Contacts:</Typography>
                          <Typography variant="body2" color="error">
                            {sync.failedContacts || 0}
                          </Typography>
                        </Box>
                        
                        {sync.syncInProgress && (
                          <LinearProgress sx={{ mb: 2 }} />
                        )}
                        
                        {sync.lastError && (
                          <Alert severity="error" sx={{ mt: 2 }}>
                            {sync.lastError}
                          </Alert>
                        )}
                      </Box>
                    )}
                    
                    <Button
                      variant="contained"
                      startIcon={<SyncIcon />}
                      onClick={() => handleSyncContacts(provider.provider)}
                      disabled={sync?.syncInProgress}
                      fullWidth
                    >
                      {sync?.syncInProgress ? 'Syncing...' : 'Sync Contacts'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          
          {providers.configured.length === 0 && (
            <Grid item xs={12}>
              <Alert severity="info">
                No providers configured. Configure a provider to start syncing contacts.
              </Alert>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Usage & Analytics Tab */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          {providers.configured.map((provider: any) => {
            const usage = usageStats[provider.provider] || provider.usage;
            
            if (!usage) {
              // Fetch usage stats if not loaded
              fetchUsageStats(provider.provider);
              return null;
            }
            
            return (
              <Grid item xs={12} md={6} key={provider.provider}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {getProviderIcon(provider.provider)} {provider.provider}
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4">{usage.emailsSent || 0}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Emails Sent
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                          <Typography variant="h4">{usage.campaignsCreated || 0}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Campaigns Created
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    {usage.currentMonth && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          Monthly Usage
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={parseFloat(usage.currentMonth.percentage || 0)}
                            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="body2">
                            {usage.currentMonth.percentage || 0}%
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {usage.currentMonth.emails} / {usage.limits?.emailsPerMonth || '∞'} emails
                        </Typography>
                      </Box>
                    )}
                    
                    {usage.lastUsed && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Last used: {new Date(usage.lastUsed).toLocaleString()}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>

      {/* Integration Guide Tab */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 3 }}>
              The RV Park system now supports full integration with major email marketing platforms. 
              Configure your preferred provider to enable advanced features like segmentation, 
              automation, and detailed analytics.
            </Alert>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🚀 Getting Started
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                    <ListItemText 
                      primary="Choose a Provider"
                      secondary="Select from Mailchimp, SendGrid, Klaviyo, and more"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                    <ListItemText 
                      primary="Configure API Keys"
                      secondary="Get API keys from your provider's dashboard"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                    <ListItemText 
                      primary="Sync Contacts"
                      secondary="Import your guest database to the provider"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                    <ListItemText 
                      primary="Start Sending"
                      secondary="Create campaigns with advanced features"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📋 Features by Provider
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Feature</TableCell>
                        <TableCell align="center">Internal</TableCell>
                        <TableCell align="center">External</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Basic Email Sending</TableCell>
                        <TableCell align="center">✅</TableCell>
                        <TableCell align="center">✅</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Advanced Segmentation</TableCell>
                        <TableCell align="center">⚠️</TableCell>
                        <TableCell align="center">✅</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>A/B Testing</TableCell>
                        <TableCell align="center">❌</TableCell>
                        <TableCell align="center">✅</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Automation Workflows</TableCell>
                        <TableCell align="center">⚠️</TableCell>
                        <TableCell align="center">✅</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Detailed Analytics</TableCell>
                        <TableCell align="center">⚠️</TableCell>
                        <TableCell align="center">✅</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Email Validation</TableCell>
                        <TableCell align="center">❌</TableCell>
                        <TableCell align="center">✅</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🔧 API Configuration
                </Typography>
                <Typography variant="body2" paragraph>
                  Each provider requires specific configuration. Here's where to find your API keys:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Mailchimp"
                      secondary="Account → Extras → API keys"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="SendGrid"
                      secondary="Settings → API Keys → Create API Key"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Klaviyo"
                      secondary="Account → Settings → API Keys"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="ActiveCampaign"
                      secondary="Settings → Developer → API Access"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Configuration Dialog */}
      <Dialog open={configDialog} onClose={() => setConfigDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Configure {selectedProvider?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedProvider && getProviderConfigFields(selectedProvider.name).map((field: any) => (
              <TextField
                key={field.name}
                fullWidth
                label={field.label}
                type={field.type}
                value={configData[field.name] || ''}
                onChange={(e) => setConfigData({ ...configData, [field.name]: e.target.value })}
                margin="normal"
                required={field.required}
                helperText={field.helperText}
              />
            ))}
            
            {testResult && (
              <Alert 
                severity={testResult.valid ? 'success' : 'error'}
                sx={{ mt: 2 }}
              >
                {testResult.valid ? 'Configuration is valid!' : testResult.error}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialog(false)}>Cancel</Button>
          <Button onClick={handleTest} disabled={!configData.apiKey}>
            Test Connection
          </Button>
          <Button 
            onClick={handleSaveConfig} 
            variant="contained"
            disabled={!configData.apiKey || (testResult && !testResult.valid)}
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailProvidersPage;